import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LayoutDashboard, Plus, RefreshCw, X, Save, ArrowLeft } from "lucide-react";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import type { ResponsiveLayouts } from "react-grid-layout/legacy";
import baseapi from "../../services/baseapi";
import { showError, showSuccess } from "../../utils/toast";
import WidgetRenderer from "./WidgetRenderer";
import AddWidgetModal from "./AddWidgetModal";
import DeleteConfirmModal from "./DeleteConfirmModal";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ResponsiveGridLayout = WidthProvider(Responsive);

interface AddWidgetPayload {
    widget_type: string;
    dataset_name: string;
    title: string;
    aggregation: string;
    metric_field: string | null;
    dimensions: string[];
    group_by_fields: string[];
    time_field: string | null;
    time_granularity: string | null;
    filters: Record<string, any>;
    custom_options: Record<string, any>;
}

interface GridItemLayout {
    i: string;
    x: number;
    y: number;
    w: number;
    h: number;
    minW?: number;
    minH?: number;
}

export default function AddWidgetPage() {
    const [searchParams] = useSearchParams();
    const dashboardId = searchParams.get("id");

    const [dashboard, setDashboard] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [savingLayout, setSavingLayout] = useState(false);
    const [isWidgetModalOpen, setIsWidgetModalOpen] = useState(false);
    const [widgetToDelete, setWidgetToDelete] = useState<any | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const navigate = useNavigate();

    const fetchDashboard = async () => {
        if (!dashboardId) return;

        setLoading(true);
        try {
            const response = await baseapi.get(`/dashboard/dashboards/${dashboardId}`, {
                params: { include_widgets: true, include_data: true }
            });
            setDashboard(response.data || null);
        } catch (error: any) {
            showError(error.message || "Failed to load dashboard");
            setDashboard(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboard();
    }, [dashboardId]);

    const currentWidgets = dashboard?.widgets || [];

    const layouts = useMemo<ResponsiveLayouts>(() => {
        const lg: GridItemLayout[] = currentWidgets.map((widget: any, index: number) => ({
            i: String(widget.widget_id),
            x: typeof widget.grid_x === "number" ? widget.grid_x : (index * 6) % 12,
            y: typeof widget.grid_y === "number" ? widget.grid_y : Infinity,
            w: typeof widget.grid_w === "number" ? widget.grid_w : 6,
            h: typeof widget.grid_h === "number" ? widget.grid_h : 4,
            minW: 1,
            minH: 1
        }));

        return {
            lg,
            md: lg,
            sm: lg,
            xs: lg,
            xxs: lg
        };
    }, [currentWidgets]);

    const handleDashboardSave = async () => {
        if (!dashboardId || !dashboard) return;

        setSavingLayout(true);
        try {
            const widgetsPayload = dashboard.widgets.map((widget: any, index: number) => ({
                widget_id: widget.is_preview ? undefined : widget.widget_id,
                widget_type: widget.widget_type,
                dataset_name: widget.dataset_name,
                title: widget.title,
                metric_field: widget.metric_field,
                aggregation: widget.aggregation,
                dimensions: widget.dimensions,
                group_by_fields: widget.group_by_fields,
                time_field: widget.time_field,
                time_granularity: widget.time_granularity,
                filters: widget.filters,
                custom_options: widget.custom_options,
                grid_x: widget.grid_x,
                grid_y: widget.grid_y,
                grid_w: widget.grid_w,
                grid_h: widget.grid_h,
                order_index: index
            }));

            const payload = {
                widgets: widgetsPayload
            };

            await baseapi.post(`/dashboard/dashboards/${dashboardId}/widgets/bulk`, payload);
            showSuccess("Dashboard saved successfully");
            fetchDashboard();
        } catch (error: any) {
            showError(error.message || "Failed to save dashboard");
        } finally {
            setSavingLayout(false);
        }
    };

    const handleLayoutSave = async (currentLayout: readonly GridItemLayout[]) => {
        setDashboard((prev: any) => {
            if (!prev) return prev;

            const updatedWidgets = prev.widgets.map((widget: any) => {
                const layoutItem = currentLayout.find(
                    (l) => String(l.i) === String(widget.widget_id)
                );
                if (!layoutItem) return widget;

                return {
                    ...widget,
                    grid_x: layoutItem.x,
                    grid_y: layoutItem.y,
                    grid_w: layoutItem.w,
                    grid_h: layoutItem.h
                };
            });

            return {
                ...prev,
                widgets: updatedWidgets
            };
        });
    };

    const handleDeleteWidget = (widgetId: string) => {
        const widget = dashboard?.widgets?.find((w: any) => String(w.widget_id) === String(widgetId));
        if (!widget) return;

        if (widget.is_preview) {
            setDashboard((prev: any) => ({
                ...prev,
                widgets: prev.widgets.filter((w: any) => String(w.widget_id) !== String(widgetId))
            }));
            return;
        }

        setWidgetToDelete(widget);
    };

    const confirmDeleteWidget = async () => {
        if (!widgetToDelete || !dashboardId) return;
        setIsDeleting(true);
        try {
            await baseapi.delete(`/dashboard/dashboards/${dashboardId}/widgets/${widgetToDelete.widget_id}`);
            showSuccess("Widget deleted successfully");
            setWidgetToDelete(null);
            fetchDashboard();
        } catch (error: any) {
            showError(error.message || "Failed to delete widget");
        } finally {
            setIsDeleting(false);
        }
    };

    const getNextWidgetPlacement = () => {
        const widgets = dashboard?.widgets || [];
        const count = widgets.length;

        return {
            grid_x: (count * 6) % 12,
            grid_y: Infinity,
            grid_w: 6,
            grid_h: 4
        };
    };

    const handleCreateWidget = async (widgetPayload: AddWidgetPayload) => {
        if (!dashboardId) return;

        setLoading(true);
        try {
            const tempId =
                typeof crypto.randomUUID === "function"
                    ? crypto.randomUUID()
                    : `preview-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

            const response = await baseapi.post("/dashboard/widgets/preview", {
                ...widgetPayload,
                widget_id: tempId,
                limit: 10
            });

            const placement = getNextWidgetPlacement();

            const previewWidget = {
                ...response.data,
                ...widgetPayload,
                ...placement,
                widget_id: tempId,
                is_preview: true
            };

            setDashboard((prev: any) => ({
                ...prev,
                widgets: [...(prev?.widgets || []), previewWidget]
            }));

            setIsWidgetModalOpen(false);
            showSuccess("Preview widget added to dashboard");
        } catch (error: any) {
            showError(error.message || "Failed to fetch widget preview");
        } finally {
            setLoading(false);
        }
    };

    if (!dashboardId) {
        return (
            <div className="p-6 text-sm text-red-400">
                Dashboard id is missing in the URL.
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full text-gray-200 p-4">
            <div className="flex items-center justify-between gap-4 mb-4 border-b border-gray-700 pb-2">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate("/custom-dashboard")}
                        className="p-2 bg-orange-600/60 rounded-lg"
                    >
                        <ArrowLeft className="w-5 h-5 text-white" />
                    </button>

                    <div className="p-2 bg-orange-600 rounded-lg">
                        <LayoutDashboard className="w-5 h-5 text-white" />
                    </div>

                    <div>
                        <h1 className="text-xl font-bold text-white">
                            {dashboard?.title || dashboard?.name || "Dashboard"}
                        </h1>
                        <p className="text-xs text-gray-400">
                            {dashboard?.description || "No description"}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {savingLayout && (
                        <span className="text-[11px] text-orange-400 font-medium">
                            Saving layout...
                        </span>
                    )}

                    <button
                        onClick={fetchDashboard}
                        disabled={loading}
                        className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all disabled:opacity-50"
                    >
                        <RefreshCw size={16} className={loading ? "animate-spin" : "text-orange-400"} />
                    </button>

                    <button
                        onClick={() => setIsWidgetModalOpen(true)}
                        className="flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                        <Plus size={16} />
                        Add Widget
                    </button>

                    <button
                        onClick={handleDashboardSave}
                        disabled={loading || savingLayout}
                        className="flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-semibold rounded-lg border border-orange-500 transition-colors disabled:opacity-50"
                    >
                        <Save size={16} />
                        Save Changes
                    </button>
                </div>
            </div>

            {loading && !dashboard ? (
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                </div>
            ) : !dashboard ? (
                <div className="text-center py-16 text-gray-400">
                    Dashboard not found
                </div>
            ) : currentWidgets.length === 0 ? (
                <div className="text-center py-16 text-gray-400 border border-dashed border-gray-700 rounded-xl">
                    <p className="text-sm font-medium">No widgets added yet</p>
                    <p className="text-xs text-gray-500 mt-1">Click “Add Widget” to create your first widget</p>
                </div>
            ) : (
                <div className="min-h-[400px]">
                    <ResponsiveGridLayout
                        className="layout"
                        layouts={layouts}
                        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                        cols={{ lg: 24, md: 20, sm: 12, xs: 8, xxs: 4 }}
                        rowHeight={40}
                        margin={[16, 16]}
                        containerPadding={[0, 0]}
                        isDraggable
                        isResizable
                        resizeHandles={["s", "w", "e", "n", "sw", "nw", "se", "ne"]}
                        onDragStop={(layout) => handleLayoutSave(layout as readonly GridItemLayout[])}
                        onResizeStop={(layout) => handleLayoutSave(layout as readonly GridItemLayout[])}
                    >
                        {currentWidgets.map((widget: any) => (
                            <div key={String(widget.widget_id)}>
                                <div
                                    className={`h-full bg-gray-900 border ${widget.is_preview
                                        ? "border-orange-500/50 shadow-orange-500/10"
                                        : "border-gray-700"
                                        } rounded-xl overflow-hidden shadow-sm transition-colors`}
                                >
                                    <div className="flex items-center justify-between px-2 py-1 border-b border-gray-800 bg-gray-800/50">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-sm font-bold text-white truncate">
                                                    {widget.title}
                                                </h3>
                                                {widget.is_preview && (
                                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-orange-500/10 text-orange-400 border border-orange-500/20">
                                                        Preview
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleDeleteWidget(String(widget.widget_id))}
                                            className="p-1 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-all"
                                            title="Remove Widget"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>

                                    <div className="p-2 h-[calc(100%-40px)]">
                                        <WidgetRenderer widget={widget} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </ResponsiveGridLayout>
                </div>
            )}

            <AddWidgetModal
                isOpen={isWidgetModalOpen}
                onClose={() => setIsWidgetModalOpen(false)}
                onSubmit={handleCreateWidget}
            />

            <DeleteConfirmModal
                isOpen={!!widgetToDelete}
                onClose={() => setWidgetToDelete(null)}
                onConfirm={confirmDeleteWidget}
                itemName={widgetToDelete?.title}
                itemType="widget"
                busy={isDeleting}
            />
        </div>
    );
}